"use client";

/**
 * Viewed listings for this account or device (GET /api/viewed), loaded
 * once per page load and shared by every card, the map markers and the
 * filter pill. Opening a listing marks it here at once; the server
 * records the same view from the listing.open event the panel already
 * sends. See docs/plans/viewed_history_plan.md.
 */
import { useEffect, useSyncExternalStore } from "react";
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";

export interface ViewedEntry {
  at: string | null;
  /** Asking price when it was last opened. */
  price: number | null;
  /** The visit before the last one (a visit = views within 30 minutes). */
  prevAt?: string | null;
  prevPrice?: number | null;
}

let entries = new Map<string, ViewedEntry>();
let state: "idle" | "loading" | "ready" = "idle";
let version = 0;
const subs = new Set<() => void>();

function emit() {
  version += 1;
  subs.forEach((f) => f());
}

function headers(): HeadersInit {
  return { "X-Client-Id": getClientId() };
}

export function loadViewed(force = false): void {
  if (typeof window === "undefined") return;
  if (state === "loading" || (state === "ready" && !force)) return;
  state = "loading";
  fetch(`${API_BASE}/viewed`, { credentials: "include", headers: headers() })
    .then((r) => (r.ok ? r.json() : { items: [] }))
    .then((d: { items?: Array<{ id: string; at: string | null; price: number | null; prev_at?: string | null; prev_price?: number | null }> }) => {
      entries = new Map((d.items ?? []).map((x) => [x.id, { at: x.at, price: x.price, prevAt: x.prev_at ?? null, prevPrice: x.prev_price ?? null }]));
      state = "ready";
      emit();
    })
    .catch(() => { state = "idle"; });
}

/** The panel opened this listing: mark it now (the server records it
 *  from the tracking event). */
export function markViewed(id: string, price: number | null | undefined): void {
  const old = entries.get(id);
  const now = new Date();
  // Same rule as the server: a view more than 30 minutes after the last
  // one makes the last one the previous visit.
  const shift = old?.at && now.getTime() - new Date(old.at).getTime() > SAME_VISIT_MS;
  entries.set(id, {
    at: now.toISOString(),
    price: price ?? null,
    prevAt: shift ? old!.at : old?.prevAt ?? null,
    prevPrice: shift ? old!.price : old?.prevPrice ?? null,
  });
  emit();
}

const SAME_VISIT_MS = 30 * 60 * 1000;

/** The visit to talk about on a listing's own page: the last one when it
 *  is older than this visit, else the one before it; null on a first
 *  visit. (The page records the visit in progress as it loads.) */
export function earlierVisit(e: ViewedEntry | undefined): { at: string; price: number | null } | null {
  if (!e?.at) return null;
  if (Date.now() - new Date(e.at).getTime() > SAME_VISIT_MS) return { at: e.at, price: e.price };
  return e.prevAt ? { at: e.prevAt, price: e.prevPrice ?? null } : null;
}

export async function clearViewed(): Promise<void> {
  entries = new Map();
  emit();
  await fetch(`${API_BASE}/viewed`, { method: "DELETE", credentials: "include", headers: headers() }).catch(() => {});
}

function subscribe(f: () => void) {
  subs.add(f);
  return () => { subs.delete(f); };
}

/** The viewed map; loads it on first use. Re-renders on changes. */
export function useViewed(): Map<string, ViewedEntry> {
  useSyncExternalStore(subscribe, () => version, () => 0);
  useEffect(() => { loadViewed(); }, []);
  return entries;
}

/** Percent change from the price at the last view, or null when it did
 *  not move (under 1%) or either side is missing. */
export function changeSinceViewed(now: number | null | undefined, then: number | null | undefined): number | null {
  if (!now || !then) return null;
  const pct = ((now - then) / then) * 100;
  return Math.abs(pct) < 1 ? null : Math.round(pct);
}
